import { Component, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { Test } from '../../../../core/interfaces/test';
import { ActivatedRoute, Router } from '@angular/router';
import { TestService } from '../../../../core/services/test.service';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../../ui/loader/loader.component';
import { CardComponent } from '../../../ui/card/card.component';
import { TranslateModule } from '@ngx-translate/core';
import { QuestionService } from '../../../../core/services/question.service';
import { BackButtonComponent } from '../../../ui/back-button/back-button.component';
import { YesNoPipe } from '../../../../shared/tools/pipes/yes-no.pipe';
import { InfoItemComponent } from '../../../ui/info-item/info-item.component';
import { AssignTestModalComponent } from '../../../ui/views/assign-test-modal/assign-test-modal.component';
import { MathTextComponent } from '../../../../shared/math/math-text.component';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';

@Component({
  selector: 'app-test-detail',
  standalone: true,
  imports: [MathTextComponent,
    LocalizedDatePipe,
    CommonModule,
    LoaderComponent,
    CardComponent,
    TranslateModule,
    BackButtonComponent,
    YesNoPipe,
    InfoItemComponent,
    AssignTestModalComponent,
  ],
  templateUrl: './test-detail.component.html',
  styleUrl: './test-detail.component.css',
})
export class TestDetailComponent {
  testId!: number;
  test!: Test;
  isLoading = true;
  error: string | null = null;

  readonly assignOpen = signal<boolean>(false);

  /**
   * Liste d'où l'on vient : l'écran sert les deux rôles depuis qu'il est
   * monté sous `/admin/test/:id` aussi. Le bouton « retour » pointait en dur
   * vers la banque de l'enseignant, ce qui éjectait l'administrateur hors de
   * son écran.
   */
  get backRoute(): string {
    return this.router.url.startsWith('/admin')
      ? '/admin/tests'
      : '/teacher/tests/bank';
  }

  private readonly auth = inject(AuthService);

  /**
   * Affecter un test, c'est écrire. Sous `/admin`, l'écran s'ouvre avec
   * `tests.read.all` : sans `tests.write.all`, la fenêtre d'affectation
   * demandait une liste d'élèves que le serveur refuse — et ce refus, sur une
   * lecture, éjectait de l'écran. Côté enseignant rien ne change : l'espace
   * est gardé par le rôle.
   */
  get canAssign(): boolean {
    return !this.router.url.startsWith('/admin') || this.auth.hasPermission('tests.write.all');
  }

  openAssign(): void { this.assignOpen.set(true); }
  closeAssign(): void { this.assignOpen.set(false); }
  onAssignmentCreated(): void {
    // could redirect or refresh; kept minimal — toast already shown by modal
    this.closeAssign();
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private testService: TestService,
    private questionService: QuestionService
  ) {}

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    if (!idParam) {
      this.error = 'Invalid test ID.';
      this.isLoading = false;
      return;
    }

    this.testId = +idParam;
    void this.loadTestWithQuestions();
  }

  async loadTestWithQuestions(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.testService.getTestWithQuestions(this.testId),
      );
      if (res?.status !== 'SUCCESS' || !res.data) {
        this.error = res?.message ?? 'Server error while fetching test details.';
        return;
      }
      this.test = res.data;

      // Complète chaque question avec ses réponses détaillées (dont la « bonne
      // réponse », absente du DTO du test). Best-effort ET par question : un
      // échec sur l'une ne doit pas masquer tout le test — l'ancienne version
      // enchaînait un Promise.all non gardé, si bien qu'une seule requête en
      // erreur bloquait la page entière (chargement infini).
      const questions = this.test.testQuestions ?? [];
      await Promise.all(
        questions.map(async (testQ) => {
          const questionId = testQ?.question?.id;
          if (!questionId) return;
          try {
            const full = await firstValueFrom(
              this.questionService.getQuestionWithAnswers(questionId),
            );
            if (full?.status === 'SUCCESS' && full.data) {
              testQ.question = full.data;
            }
          } catch {
            // On garde la question de base (titre + réponses éventuelles) ; le
            // test reste affiché même si l'enrichissement d'une question échoue.
          }
        }),
      );
    } catch {
      this.error = 'Server error while fetching test details.';
    } finally {
      this.isLoading = false;
    }
  }

  // Méthode pour traduire TestMode
  translateTestMode(testMode: string): string {
    const translations: { [key: string]: string } = {
      exam: 'إختبار عادي',
      practice: 'تدريب',
      evaluation: 'إختبار تشخيصي',
    };
    return translations[testMode] || testMode;
  }
}
